import { Backdrop, ClickAwayListener, Icon, Stack, Tooltip, Typography } from '@mui/material';
import { Facility } from '@schooly/api';
import { DROPDOWN } from '@schooly/constants/test-ids';
import {
  RoomSelectContent,
  SelectContentSkeleton,
  SelectedRoom,
  SelectSearchInput,
} from '@schooly/components/filters';
import { useFlag } from '@schooly/hooks/use-flag';
import { ArrowDownV2Icon } from '@schooly/style';
import { FC, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { TimetableRoom } from '../types';

const CELL_HEIGHT = 45;

type RoomCellSelectProps = PropsWithChildren<{
  selectedRoom: TimetableRoom | null;
  facilities: Facility[];
  isLoading: boolean;
  onSelect: (room: TimetableRoom | null) => void;
}>;

export const RoomCellSelect: FC<RoomCellSelectProps> = ({
  selectedRoom,
  facilities,
  isLoading,
  onSelect,
  children,
}) => {
  const { formatMessage } = useIntl();
  const [opened, open, close] = useFlag();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  const handleSelect = useCallback(
    (room: SelectedRoom | null) => {
      onSelect(room);
      close();
    },
    [onSelect, close],
  );

  const handleClose = useCallback(() => {
    close();
    setQuery('');
  }, [close]);

  return (
    <>
      <Backdrop
        open={opened}
        invisible
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, cursor: 'default' }}
        onClick={handleClose}
      />
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <Tooltip
            onClose={handleClose}
            open={opened}
            placement="bottom-start"
            componentsProps={{
              tooltip: {
                sx: (theme) => ({
                  maxWidth: 'none',
                  border: `1px solid ${theme.palette.common.light3}`,
                  padding: 0,
                  overflow: 'hidden',
                  willChange: 'transform',
                }),
              },
            }}
            slotProps={{
              popper: {
                sx: (theme) => ({
                  '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
                    marginTop: `${-CELL_HEIGHT - 1}px`,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0,
                    '.headerCell': { borderBottom: theme.mixins.borderControlValue() },
                  },
                  '&.MuiTooltip-popper[data-popper-placement*="top"] .MuiTooltip-tooltip': {
                    marginBottom: `${-CELL_HEIGHT - 1}px`,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    '.headerCell': { order: 1, borderTop: theme.mixins.borderControlValue() },
                  },
                }),
              },
            }}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={
              <Stack>
                <HeaderCell onClick={handleClose}>
                  <Stack
                    direction="row"
                    onClick={(e) => {
                      e.stopPropagation();
                      searchInputRef.current?.focus();
                    }}
                    sx={{ flex: 1, cursor: 'text' }}
                  >
                    <SelectSearchInput
                      ref={searchInputRef}
                      autoFocus
                      value={query}
                      onChangeText={setQuery}
                      debounced={false}
                    />
                    {!!selectedRoom && !query && (
                      <Typography noWrap ml={-1.5} color="primary.main">
                        {selectedRoom.name}
                      </Typography>
                    )}
                  </Stack>
                  <ArrowIcon rotated={opened} />
                </HeaderCell>

                {isLoading ? (
                  <SelectContentSkeleton />
                ) : (
                  <RoomSelectContent
                    selectedRoom={selectedRoom}
                    facilities={facilities}
                    query={query}
                    onSelectRoom={handleSelect}
                  />
                )}
              </Stack>
            }
          >
            <div data-test-id={DROPDOWN.ROOM}>
              <HeaderCell onClick={open}>
                <Typography noWrap color={selectedRoom ? undefined : 'common.grey'}>
                  {selectedRoom ? selectedRoom.name : formatMessage({ id: 'timetable-SelectRoom' })}
                </Typography>
                <Stack direction="row" alignItems="center" gap={1}>
                  {children}
                  <ArrowIcon rotated={opened} />
                </Stack>
              </HeaderCell>
            </div>
          </Tooltip>
        </div>
      </ClickAwayListener>
    </>
  );
};

type HeaderCellProps = {
  onClick: () => void;
} & PropsWithChildren;

const HeaderCell: FC<HeaderCellProps> = ({ onClick, children }) => (
  <Stack
    className="headerCell"
    onClick={onClick}
    sx={{
      height: CELL_HEIGHT,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      px: 1,
      cursor: 'pointer',
      gap: 1,
    }}
  >
    {children}
  </Stack>
);

const ArrowIcon: FC<{ rotated?: boolean }> = ({ rotated }) => (
  <Icon
    sx={(theme) => ({
      width: theme.spacing(1),
      transform: rotated ? 'rotate(180deg)' : undefined,
      path: { stroke: theme.palette.common.grey },
    })}
  >
    <ArrowDownV2Icon />
  </Icon>
);
